import { ICall, ITranscriptMessage } from './types/callsTypes'
// eslint-disable-next-line import/named
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
// types
import { ITakeOver, NoTakeOver, TakeOverTypeEnum } from '@outbound-ai/softphone'
import { RootState } from 'appState/store'
import { updateCallNotesApi } from 'features/calls/callsApi'
import { IUserPhone } from '../settings/settingsTypes'
import { AutomationTypeEnum, CallTypeEnum } from './types/callsEnums'

interface ICallsState {
  call: null | ICall
  talkToPayorRepLiveCall: {
    showDialPad: boolean
    transcript: ITranscriptMessage[]
    automationType: AutomationTypeEnum | null
    startTime: string
    isConnected: boolean
    takeOver: ITakeOver
    availableTakeOverType: TakeOverTypeEnum
    userTakeOverPhone: IUserPhone | null
    isMuted: boolean
    isMutedAudio: boolean
    elapsedTime: string
    type: CallTypeEnum | null
    isActivePayorAgentReady: boolean
    activeCallJobId: string
    activeClaimId: string
    lastCallJobIdUserCompleted?: string
    shouldRedirectIfCallTakenOver: boolean
    isHumanAgentCallInProgress?: boolean | undefined
    claimId: string
    activeCallJbId: string
  }
  ongoingCalls: ICall[]
}

export const callsSliceLiveCallInitialState = {
  transcript: [],
  startTime: '',
  takeOver: NoTakeOver,
  availableTakeOverType: TakeOverTypeEnum.none,
  userTakeOverPhone: null,
  isMuted: true,
  isConnected: false,
  isMutedAudio: false,
  automationType: null,
  elapsedTime: '0:00:00',
  type: null,
  showDialPad: false,
  isConductingPayorAgentCall: false,
  isActivePayorAgentReady: false,
  activeCallJobId: '',
  activeClaimId: '',
  shouldRedirectIfCallTakenOver: false,
  claimId: '',
  activeCallJbId: '',
}

export const callsSliceInitialState = {
  call: null,
  talkToPayorRepLiveCall: callsSliceLiveCallInitialState,
  ongoingCalls: [],
} as ICallsState

interface IUpdateCallNotes {
  callId: string
  notes: string
}

type UpdateLiveCallRequestType = {
  [key in keyof ICallsState['talkToPayorRepLiveCall']]?: ICallsState['talkToPayorRepLiveCall'][key]
}

export const updateCallNotes = createAsyncThunk<ICall, IUpdateCallNotes, { state: RootState }>(
  'calls/updateCallNotesByCallId',
  async ({ callId, notes }: IUpdateCallNotes): Promise<ICall> => {
    const response = await updateCallNotesApi(callId, notes)
    return response.data
  },
)

export const callsSlice = createSlice({
  name: 'calls',
  initialState: callsSliceInitialState,
  reducers: {
    updateDialPadVisibility(state: ICallsState, action: PayloadAction<boolean>) {
      state.talkToPayorRepLiveCall.showDialPad = action.payload
    },
    updateLiveCall(state: ICallsState, action: PayloadAction<UpdateLiveCallRequestType>) {
      state.talkToPayorRepLiveCall = { ...state.talkToPayorRepLiveCall, ...action.payload }
    },
    updateHumanAgentCallInProgress(state: ICallsState, action: PayloadAction<{ callInProgress: boolean }>) {
      state.talkToPayorRepLiveCall.isHumanAgentCallInProgress = action.payload.callInProgress
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateCallNotes.fulfilled, (state, action) => {
      if (action.payload) state.call = action.payload
    })
  },
})

export const { updateDialPadVisibility, updateLiveCall, updateHumanAgentCallInProgress } = callsSlice.actions

export const callsReducer = callsSlice.reducer
